import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cryptoPortfolioService,
  CryptoPortfolioActivityBot,
  ActivityBotAiTuneHistoryEntry,
} from '../../services/cryptoPortfolioService';
import AiTuneChangeTable from '../admin/AiTuneChangeTable';
import AiTunePerformanceMetaPanels from '../admin/AiTunePerformanceMetaPanels';
import ActivityBotAiTunePerformanceSnapshot from './ActivityBotAiTunePerformanceSnapshot';
import ActivityBotAiTuneRegimeReview from './ActivityBotAiTuneRegimeReview';
import '../admin/AdminComponents.css';

type Tab = 'settings' | 'performance' | 'history' | 'review';

export interface ActivityBotAiAutoTuneModalProps {
  bot: CryptoPortfolioActivityBot;
  open: boolean;
  onClose: () => void;
  onBotUpdated: (bot: CryptoPortfolioActivityBot) => void;
}

function formatInterval(ms?: number): string {
  if (!ms || ms <= 0) return '—';
  const mins = Math.round(ms / 60_000);
  return mins >= 60 ? `${Math.round(mins / 60)}h` : `${mins} min`;
}

function formatLastApplied(ts?: number | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

const ActivityBotAiAutoTuneModal: React.FC<ActivityBotAiAutoTuneModalProps> = ({
  bot,
  open,
  onClose,
  onBotUpdated,
}) => {
  const [tab, setTab] = useState<Tab>('settings');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [globalSettings, setGlobalSettings] = useState<Record<string, unknown> | null>(null);
  const [pending, setPending] = useState<Record<string, unknown> | null>(null);
  const [history, setHistory] = useState<ActivityBotAiTuneHistoryEntry[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState<number | null>(null);

  const [form, setForm] = useState({
    aiAutoTuneEnabled: Boolean(bot.aiAutoTuneEnabled),
    aiAutoTuneRequireApproval: bot.aiAutoTuneRequireApproval !== false,
    aiPerformanceTuneEnabled: Boolean(bot.aiPerformanceTuneEnabled),
    aiPerformanceTuneWindow: bot.aiPerformanceTuneWindow ?? 12,
    aiPerformanceTuneMinTrades: bot.aiPerformanceTuneMinTrades ?? 8,
    aiPerformanceSideBlockEnabled: Boolean(bot.aiPerformanceSideBlockEnabled),
    aiPerformanceTuneLlmEnabled: Boolean(bot.aiPerformanceTuneLlmEnabled),
    aiPerformanceTuneHistoryLearningEnabled: bot.aiPerformanceTuneHistoryLearningEnabled !== false,
  });

  const [regimeGates, setRegimeGates] = useState({
    lowParticipationMode: bot.lowParticipationMode || 'REDUCED_EXPOSURE',
    lowParticipationAutoBlockDays: bot.lowParticipationAutoBlockDays ?? 0,
    aiRegimeLongBlockEnabled: Boolean(bot.aiRegimeLongBlockEnabled),
    aiRegimeLongBlockList: bot.aiRegimeLongBlockList || 'BEARISH_BTC',
    scalperModeEnabled: Boolean(bot.scalperModeEnabled),
    scalperTakeProfit: '1.5',
    scalperTrailingStopLoss: '0.6',
    scalperMaxOpenPositions: '2',
    scalperRegimeList: 'HIGH_VOLATILITY',
  });

  const botTitle = bot.referenceName?.trim() || bot.displayLabel || `Bot #${bot.id}`;

  const loadPending = useCallback(async () => {
    const res = await cryptoPortfolioService.getActivityBotAiAutoTunePending(bot.id);
    setPending(res.pending ?? null);
  }, [bot.id]);

  const loadHistory = useCallback(async (page: number) => {
    setHistoryLoading(true);
    try {
      const res = await cryptoPortfolioService.getActivityBotAiAutoTuneHistory(bot.id, page, 10);
      setHistory(res.items ?? []);
      setHistoryTotalPages(Math.max(res.totalPages ?? 1, 1));
      setHistoryPage(page);
    } finally {
      setHistoryLoading(false);
    }
  }, [bot.id]);

  useEffect(() => {
    if (!open) return;
    setTab('settings');
    setError(null);
    setForm({
      aiAutoTuneEnabled: Boolean(bot.aiAutoTuneEnabled),
      aiAutoTuneRequireApproval: bot.aiAutoTuneRequireApproval !== false,
      aiPerformanceTuneEnabled: Boolean(bot.aiPerformanceTuneEnabled),
      aiPerformanceTuneWindow: bot.aiPerformanceTuneWindow ?? 12,
      aiPerformanceTuneMinTrades: bot.aiPerformanceTuneMinTrades ?? 8,
      aiPerformanceSideBlockEnabled: Boolean(bot.aiPerformanceSideBlockEnabled),
      aiPerformanceTuneLlmEnabled: Boolean(bot.aiPerformanceTuneLlmEnabled),
      aiPerformanceTuneHistoryLearningEnabled: bot.aiPerformanceTuneHistoryLearningEnabled !== false,
    });
    setRegimeGates({
      lowParticipationMode: bot.lowParticipationMode || 'REDUCED_EXPOSURE',
      lowParticipationAutoBlockDays: bot.lowParticipationAutoBlockDays ?? 0,
      aiRegimeLongBlockEnabled: Boolean(bot.aiRegimeLongBlockEnabled),
      aiRegimeLongBlockList: bot.aiRegimeLongBlockList || 'BEARISH_BTC',
      scalperModeEnabled: Boolean(bot.scalperModeEnabled),
      scalperTakeProfit: '1.5',
      scalperTrailingStopLoss: '0.6',
      scalperMaxOpenPositions: '2',
      scalperRegimeList: 'HIGH_VOLATILITY',
    });
    void cryptoPortfolioService.getActivityBotAiAutoTuneGlobalSettings().then(setGlobalSettings);
    void loadPending();
    void loadHistory(0);
  }, [open, bot, loadPending, loadHistory]);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const updated = await cryptoPortfolioService.updateActivityBotAiAutoTuneSettings(bot.id, {
        ...form,
        ...regimeGates,
        lowParticipationAutoBlockDays: Number(regimeGates.lowParticipationAutoBlockDays) || 0,
        scalperTakeProfit: Number(regimeGates.scalperTakeProfit) || 1.5,
        scalperTrailingStopLoss: Number(regimeGates.scalperTrailingStopLoss) || 0.6,
        scalperMaxOpenPositions: Number(regimeGates.scalperMaxOpenPositions) || 2,
      });
      onBotUpdated(updated);
      await loadPending();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    setError(null);
    try {
      const result = await cryptoPortfolioService.runActivityBotAiAutoTuneNow(bot.id);
      if (result.bot) {
        onBotUpdated(result.bot);
      }
      await loadPending();
      await loadHistory(0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Run now failed');
    } finally {
      setRunning(false);
    }
  };

  const handleApplyPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const updated = await cryptoPortfolioService.applyActivityBotAiAutoTunePending(bot.id);
      onBotUpdated(updated);
      setPending(null);
      await loadHistory(0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Apply failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDismissPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const updated = await cryptoPortfolioService.dismissActivityBotAiAutoTunePending(bot.id);
      onBotUpdated(updated);
      setPending(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Dismiss failed');
    } finally {
      setLoading(false);
    }
  };

  const pendingChanges = useMemo(() => {
    if (!pending?.proposedChanges || typeof pending.proposedChanges !== 'object') return [];
    const proposed = pending.proposedChanges as Record<string, unknown>;
    return Object.entries(proposed).map(([field, after]) => ({
      field,
      label: field,
      before: '—',
      after: String(after ?? '—'),
    }));
  }, [pending]);

  if (!open) return null;

  const modalWidth = tab === 'review' ? 920 : tab === 'history' ? 720 : tab === 'performance' ? 680 : 560;

  return (
    <div className="modal-overlay ai-auto-tune-modal-overlay" onClick={onClose}>
      <div
        className="modal-content ai-auto-tune-modal-content"
        style={{ maxWidth: modalWidth }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <h3 className="modal-title">🤖 AI Auto-Tune — {botTitle}</h3>
          <button type="button" onClick={onClose} className="modal-close">×</button>
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '0 20px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          {(['settings', 'performance', 'history', 'review'] as Tab[]).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTab(t);
                if (t === 'history') void loadHistory(historyPage);
              }}
              style={{
                padding: '8px 14px',
                borderRadius: 999,
                border: tab === t ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                background: tab === t ? '#faf5ff' : '#fff',
                color: tab === t ? '#6d28d9' : '#374151',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="modal-body ai-auto-tune-modal-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {error && (
            <div style={{ marginBottom: 12, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#b91c1c', fontSize: 13 }}>
              {error}
            </div>
          )}

          {tab === 'review' ? (
            <ActivityBotAiTuneRegimeReview botId={bot.id} active={tab === 'review'} />
          ) : tab === 'performance' ? (
            <ActivityBotAiTunePerformanceSnapshot botId={bot.id} active={tab === 'performance'} />
          ) : tab === 'history' ? (
            <div>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
                Before/after values for each AI auto-tune event on this activity bot.
              </p>
              {historyLoading && <p style={{ fontSize: 13, color: '#6b7280' }}>Loading history…</p>}
              {!historyLoading && history.length === 0 && (
                <div style={{ padding: 16, background: '#f9fafb', borderRadius: 8, color: '#6b7280', fontSize: 13 }}>
                  No AI tune history yet. Run AI Tune or wait for the scheduler after enabling auto-tune.
                </div>
              )}
              {history.map(entry => {
                const expanded = expandedHistoryId === entry.id;
                const changes = entry.changes ?? [];
                return (
                  <div key={entry.id} style={{ marginBottom: 12, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                    <button
                      type="button"
                      onClick={() => setExpandedHistoryId(expanded ? null : entry.id)}
                      style={{ width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', background: '#f8fafc', cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 13 }}>{entry.appliedAt ? new Date(entry.appliedAt).toLocaleString() : '—'}</strong>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          {entry.marketRegime || '—'} · {entry.outcome || '—'} · {entry.trigger || '—'}
                        </span>
                      </div>
                    </button>
                    {expanded && (
                      <div style={{ padding: 12 }}>
                        <AiTunePerformanceMetaPanels meta={entry.performanceMeta} />
                        <AiTuneChangeTable changes={changes} />
                      </div>
                    )}
                  </div>
                );
              })}
              {historyTotalPages > 1 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                  <button type="button" disabled={historyLoading || historyPage <= 0} onClick={() => void loadHistory(historyPage - 1)}>Prev</button>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>Page {historyPage + 1} / {historyTotalPages}</span>
                  <button type="button" disabled={historyLoading || historyPage + 1 >= historyTotalPages} onClick={() => void loadHistory(historyPage + 1)}>Next</button>
                </div>
              )}
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
                Applies only to this activity bot. Position size (USD per leg) and sector are never auto-changed.
              </p>
              <div style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: 8,
                backgroundColor: bot.aiAutoTuneEnabled ? '#f0fdf4' : '#f9fafb',
                border: `1px solid ${bot.aiAutoTuneEnabled ? '#86efac' : '#e5e7eb'}`,
              }}>
                <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700 }}>
                  Saved state: {bot.aiAutoTuneEnabled ? '✅ Auto-tune ON' : '⭕ Auto-tune OFF'}
                </p>
                {bot.aiAutoTuneEnabled && (
                  <p style={{ margin: '4px 0', fontSize: 12, color: '#444' }}>
                    Last regime: <strong>{bot.aiAutoTuneLastRegime || '—'}</strong> · Last applied: <strong>{formatLastApplied(bot.aiAutoTuneLastAppliedAt)}</strong>
                  </p>
                )}
                {bot.aiAutoTunePending && (
                  <p style={{ margin: '6px 0 0', fontSize: 12, color: '#b45309', fontWeight: 600 }}>⏳ Pending approval required</p>
                )}
              </div>

              <div style={{
                marginBottom: 12,
                padding: '10px 14px',
                borderRadius: 8,
                fontSize: 12,
                backgroundColor: globalSettings?.schedulerEnabled ? '#ecfdf5' : '#f8fafc',
                border: `1px solid ${globalSettings?.schedulerEnabled ? '#86efac' : '#e2e8f0'}`,
              }}>
                <strong>Automatic re-runs (server scheduler):</strong>{' '}
                {globalSettings?.schedulerEnabled
                  ? `ON — evaluates enabled bots every ~${formatInterval(globalSettings.schedulerIntervalMs as number)}`
                  : 'OFF — use Run Now or set activity.bot.ai.auto-tune.scheduler.enabled=true'}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', marginBottom: 12, backgroundColor: form.aiAutoTuneEnabled ? '#ede9fe' : '#f9fafb', border: form.aiAutoTuneEnabled ? '1px solid #7c3aed' : '1px solid #e5e7eb', borderRadius: 8 }}>
                <input
                  type="checkbox"
                  checked={form.aiAutoTuneEnabled}
                  onChange={e => setForm({ ...form, aiAutoTuneEnabled: e.target.checked })}
                  style={{ width: 18, height: 18, accentColor: '#7c3aed' }}
                />
                <label style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Enable live AI auto-tune for this bot</label>
              </div>

              {form.aiAutoTuneEnabled && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 700, margin: '0 0 8px' }}>Apply mode (per bot — choose one)</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, aiAutoTuneRequireApproval: true })}
                        style={{ textAlign: 'left', padding: 12, borderRadius: 8, border: form.aiAutoTuneRequireApproval ? '2px solid #7c3aed' : '1px solid #e5e7eb', background: form.aiAutoTuneRequireApproval ? '#faf5ff' : '#fff', cursor: 'pointer' }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 13 }}>🛡️ Manual approve</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Run Now or scheduler → AI Pending → you Apply Pending.</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, aiAutoTuneRequireApproval: false })}
                        style={{ textAlign: 'left', padding: 12, borderRadius: 8, border: !form.aiAutoTuneRequireApproval ? '2px solid #7c3aed' : '1px solid #e5e7eb', background: !form.aiAutoTuneRequireApproval ? '#faf5ff' : '#fff', cursor: 'pointer' }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 13 }}>⚡ Auto-apply</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Run Now or scheduler → config updates immediately.</div>
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>Trade performance feedback (closed legs)</p>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={form.aiPerformanceTuneEnabled}
                        onChange={e => setForm({ ...form, aiPerformanceTuneEnabled: e.target.checked })}
                      />
                      Use trade performance feedback when tuning parameters
                    </label>
                    {form.aiPerformanceTuneEnabled && (
                      <>
                        <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                          <label style={{ fontSize: 12, color: '#64748b' }}>
                            Window (trades){' '}
                            <select
                              value={form.aiPerformanceTuneWindow}
                              onChange={e => setForm({ ...form, aiPerformanceTuneWindow: Number(e.target.value) })}
                              style={{ marginLeft: 4 }}
                            >
                              {[5, 10, 12, 20].map(w => (
                                <option key={w} value={w}>{w}</option>
                              ))}
                            </select>
                          </label>
                          <label style={{ fontSize: 12, color: '#64748b' }}>
                            Min trades{' '}
                            <input
                              type="number"
                              min={3}
                              max={50}
                              value={form.aiPerformanceTuneMinTrades}
                              onChange={e => setForm({ ...form, aiPerformanceTuneMinTrades: Number(e.target.value) || 8 })}
                              style={{ width: 48, marginLeft: 4 }}
                            />
                          </label>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                          <input
                            type="checkbox"
                            id="activityBotAiPerformanceTuneLlmEnabled"
                            checked={form.aiPerformanceTuneLlmEnabled}
                            disabled={!globalSettings?.performanceLlmEnabled}
                            onChange={e => setForm({ ...form, aiPerformanceTuneLlmEnabled: e.target.checked })}
                            style={{ width: 16, height: 16 }}
                          />
                          <label htmlFor="activityBotAiPerformanceTuneLlmEnabled" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            LLM advisor for richer performance reasoning (optional)
                          </label>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                          <input
                            type="checkbox"
                            id="activityBotAiPerformanceTuneHistoryLearningEnabled"
                            checked={form.aiPerformanceTuneHistoryLearningEnabled}
                            disabled={!globalSettings?.performanceHistoryLearningEnabled}
                            onChange={e => setForm({ ...form, aiPerformanceTuneHistoryLearningEnabled: e.target.checked })}
                            style={{ width: 16, height: 16 }}
                          />
                          <label htmlFor="activityBotAiPerformanceTuneHistoryLearningEnabled" style={{ fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            Learn from tune history (LLM context + skip repeated ineffective changes)
                          </label>
                        </div>
                        {form.aiPerformanceTuneHistoryLearningEnabled ? (
                          <p style={{ fontSize: 11, color: '#64748b', margin: '6px 0 0', lineHeight: 1.4 }}>
                            Feeds recent tune outcomes into the LLM prompt and blocks repeats that did not improve net PnL.
                          </p>
                        ) : null}
                        {!globalSettings?.performanceLlmEnabled ? (
                          <p style={{ fontSize: 11, color: '#b45309', margin: '6px 0 0' }}>
                            Server LLM off — set <code>trading.ai.performance-tune.llm.enabled=true</code> and API key to use.
                          </p>
                        ) : globalSettings?.performanceLlmConfigured === false ? (
                          <p style={{ fontSize: 11, color: '#b45309', margin: '6px 0 0' }}>
                            LLM enabled on server but API key missing ({String(globalSettings?.performanceLlmProvider ?? 'openai')} / {String(globalSettings?.performanceLlmModel ?? '—')}).
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>

                  <div style={{ marginBottom: 14, padding: 12, border: `1px solid ${regimeGates.lowParticipationMode === 'ENTRY_BLOCK' ? '#fca5a5' : '#e5e7eb'}`, borderRadius: 8 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700 }}>LOW_PARTICIPATION behaviour</p>
                    {(['REDUCED_EXPOSURE', 'ENTRY_BLOCK'] as const).map(mode => (
                      <label key={mode} style={{ display: 'block', marginBottom: 8, fontSize: 12, cursor: 'pointer' }}>
                        <input
                          type="radio"
                          checked={regimeGates.lowParticipationMode === mode}
                          onChange={() => setRegimeGates({ ...regimeGates, lowParticipationMode: mode })}
                        />{' '}
                        {mode === 'REDUCED_EXPOSURE'
                          ? 'Reduced exposure (2 positions, tighter filters) — default'
                          : 'Full entry block (no new pairs, monitor only)'}
                      </label>
                    ))}
                    <label style={{ fontSize: 12, color: '#64748b' }}>
                      Auto-escalate to ENTRY_BLOCK after{' '}
                      <input
                        type="number"
                        min={0}
                        value={regimeGates.lowParticipationAutoBlockDays}
                        onChange={e => setRegimeGates({ ...regimeGates, lowParticipationAutoBlockDays: Number(e.target.value) || 0 })}
                        style={{ width: 48, margin: '0 4px' }}
                      />
                      consecutive days (0 = disabled)
                    </label>
                  </div>

                  <div style={{ marginBottom: 14, padding: 12, border: `1px solid ${regimeGates.aiRegimeLongBlockEnabled ? '#dc2626' : '#e5e7eb'}`, borderRadius: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={regimeGates.aiRegimeLongBlockEnabled}
                        onChange={e => setRegimeGates({ ...regimeGates, aiRegimeLongBlockEnabled: e.target.checked })}
                      />
                      BEARISH_BTC — Short-only mode (Long Block): {regimeGates.aiRegimeLongBlockEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>

                  <div style={{ padding: 12, border: `1px solid ${regimeGates.scalperModeEnabled ? '#7c3aed' : '#e5e7eb'}`, borderRadius: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={regimeGates.scalperModeEnabled}
                        onChange={e => setRegimeGates({ ...regimeGates, scalperModeEnabled: e.target.checked })}
                      />
                      Scalper mode (HIGH_VOLATILITY): {regimeGates.scalperModeEnabled ? 'Enabled' : 'Disabled'}
                    </label>
                  </div>

                  {pending && pendingChanges.length > 0 && (
                    <div style={{ marginTop: 16, padding: 12, border: '1px solid #fbbf24', borderRadius: 8, background: '#fffbeb' }}>
                      <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: '#b45309' }}>Pending AI changes</p>
                      <AiTunePerformanceMetaPanels
                        meta={{
                          performanceSnapshot: pending.performanceSnapshot,
                          triggeredPerformanceRules: pending.triggeredPerformanceRules,
                          performanceReasoning: pending.performanceReasoning,
                          llmUsed: pending.llmUsed,
                          llmFallback: pending.llmFallback,
                          llmSkipReason: pending.llmSkipReason,
                          llmReasoning: pending.llmReasoning,
                          llmFieldAdjustments: pending.llmFieldAdjustments,
                          llmProvider: pending.llmProvider,
                          historyLearningBlocked: pending.historyLearningBlocked,
                          llmHistoryContextUsed: pending.llmHistoryContextUsed,
                          llmHistoryContextEventCount: pending.llmHistoryContextEventCount,
                          llmRecentTuneOutcomes: pending.llmRecentTuneOutcomes,
                        }}
                      />
                      <AiTuneChangeTable changes={pendingChanges} compact />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '12px 20px', borderTop: '1px solid #e5e7eb', flexShrink: 0, flexWrap: 'wrap' }}>
          <button type="button" onClick={onClose} className="action-btn" style={{ background: '#6b7280', color: '#fff', border: 'none' }}>Close</button>
          <button type="button" onClick={() => void handleRunNow()} disabled={running || loading} className="action-btn" style={{ background: '#0891b2', color: '#fff', border: 'none' }}>
            {running ? 'Running…' : 'Run Now'}
          </button>
          <button type="button" onClick={() => void handleSave()} disabled={loading} className="action-btn" style={{ background: '#7c3aed', color: '#fff', border: 'none' }}>Save Settings</button>
          {pending && (
            <>
              <button type="button" onClick={() => void handleDismissPending()} disabled={loading} className="action-btn" style={{ background: '#dc2626', color: '#fff', border: 'none' }}>Dismiss</button>
              <button type="button" onClick={() => void handleApplyPending()} disabled={loading} className="action-btn" style={{ background: '#16a34a', color: '#fff', border: 'none' }}>Apply Pending</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityBotAiAutoTuneModal;
